<template>
	<Layout>
		<div class="l-container">
			<SectionTitle
				class="u-a11y-visually-hidden"
				:title="$I18n.trans('arena.video_center.title')"
				type="main"
			/>
			<div class="video-center">
				<LoadingSpinner
					v-if="isLoading"
					class="video-center__loading"
					color="dark"
				/>
				<template v-else-if="harness">
					<VideoCenterNavigation
						v-if="!hasUrlPresetFilters"
						:key="navigationKey"
						:has-events="harness.contextConfig.hasEvents"
						:show-live-upcoming="harness.contextConfig.liveUpcomingEnabled"
						:primary-view="primaryView"
						:library-mode="libraryMode"
						:filters="navigationFilters"
						:search-value="searchValue"
						@primary-view-change="handlePrimaryViewChange"
						@library-mode-change="handleLibraryModeChange"
						@filter-change="handleFilterChange"
						@search-change="handleSearchChange"
					/>
					<NavigationFilters
						v-else
						class="video-center__preset-search"
						:filters="[]"
						:search-value="searchValue"
						@search-change="handleSearchChange"
					/>
					<VideoList
						:key="videoListKey"
						:context-filter="harness.contextConfig.contextFilter"
						:event-filters="videoListEventFilters"
						:external-sort-order="videoListSortOrder"
						:show-results-count="true"
					/>
				</template>
			</div>
		</div>
	</Layout>
</template>

<script>
import Layout from '@js/Views/Layout';
import SectionTitle from '@js/components/SectionTitle';
import VideoCenterNavigation from '@js/components/VideoCenter/VideoCenterNavigation.vue';
import NavigationFilters from '@js/components/NavigationFilters.vue';
import VideoList from '@js/components/VideoList.vue';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import { EventFilter } from '@shared/enums/EventFilter';
import { EventOrder } from '@shared/enums/EventOrder';
import { EventType } from '@shared/enums/EventType';
import { httpClient } from '@shared/js/api/httpClient';
import { parseVideoCenterUrlPreset } from '@js/components/VideoCenter/videoCenterUrlPreset';

export default {
	name:       'ViewVideoCenter',
	components: { Layout, LoadingSpinner, NavigationFilters, SectionTitle, VideoCenterNavigation, VideoList },
	props:      {
		contextType: {
			type:     String,
			required: true,
		},
		contextId: {
			type:     Number,
			required: true,
		},
	},
	data() {
		return {
			harness:            null,
			isLoading:          true,
			primaryView:        'replays-and-videos',
			libraryMode:        'all',
			searchValue:        '',
			selectedFilters:    {},
			activeFilters:      [],
			filtersRequestId:   0,
			urlPresetFilters:   null,
			urlPresetSortOrder: null,
		};
	},
	computed: {
		navigationKey() {
			return JSON.stringify({
				hasEvents:        this.harness?.contextConfig?.hasEvents,
				showLiveUpcoming: this.harness?.contextConfig?.liveUpcomingEnabled,
				filters:          this.navigationFilters.map(filter => ({
					key:   filter.key,
					value: this.selectedFilters[filter.key] ?? filter.defaultValue,
				})),
			});
		},
		videoListKey() {
			return JSON.stringify({
				primaryView: this.primaryView,
				libraryMode: this.libraryMode,
				searchValue: this.searchValue,
				filters:     this.navigationFilters.map(filter => ({
					key:   filter.key,
					value: this.selectedFilters[filter.key] ?? filter.defaultValue,
				})),
				sortOrder:          this.videoListSortOrder,
				urlPresetFilters:   this.urlPresetFilters,
				urlPresetSortOrder: this.urlPresetSortOrder,
			});
		},
		hasUrlPresetFilters() {
			return this.urlPresetFilters !== null;
		},
		navigationFilters() {
			return (this.activeFilters || []).map(filter => ({
				...filter,
				value: this.selectedFilters[filter.key] ?? filter.defaultValue ?? null,
			}));
		},
		resolvedEventFilters() {
			const filters = {};

			if (this.searchValue) {
				filters[EventFilter.SEARCH] = this.searchValue;
			}

			const filterToEventFilterMap = {
				sport:        EventFilter.SPORT,
				club:         EventFilter.CLUB,
				league:       EventFilter.LEAGUE,
				team:         EventFilter.TEAM,
				teamCategory: EventFilter.TEAM_CATEGORY,
				eventTopic:   EventFilter.CATEGORY_CHILD,
			};

			Object.entries(this.selectedFilters).forEach(([key, value]) => {
				if ((value === null || value === '') || !filterToEventFilterMap[key]) {
					return;
				}

				filters[filterToEventFilterMap[key]] = value;
			});

			if (!this.harness?.contextConfig?.hasEvents && !this.harness?.contextConfig?.liveUpcomingEnabled) {
				filters[EventFilter.CONTENT_TYPE] = [EventType.UPLOAD, EventType.CLIP];
				return filters;
			}

			if (this.harness?.contextConfig?.liveUpcomingEnabled && this.primaryView === 'live-and-upcoming') {
				filters[EventFilter.EVENT] = null;
				filters[EventFilter.NOT_ENDED] = null;
				return filters;
			}

			if (this.libraryMode === 'replays') {
				filters[EventFilter.EVENT] = null;
				filters[EventFilter.ENDED] = null;
				return filters;
			}

			if (this.libraryMode === 'videos') {
				filters[EventFilter.CONTENT_TYPE] = [EventType.UPLOAD, EventType.CLIP];
				return filters;
			}

			filters[EventFilter.VIDEO] = null;
			return filters;
		},
		videoListEventFilters() {
			if (!this.hasUrlPresetFilters) {
				return this.resolvedEventFilters;
			}

			const filters = { ...this.urlPresetFilters };
			if (this.searchValue) {
				filters[EventFilter.SEARCH] = this.searchValue;
			}

			return filters;
		},
		videoListSortOrder() {
			return this.hasUrlPresetFilters && this.urlPresetSortOrder
				? this.urlPresetSortOrder
				: this.resolvedSortOrder;
		},
		resolvedSortOrder() {
			return {
				[EventOrder.START]: this.primaryView === 'live-and-upcoming' ? 'asc' : 'desc',
			};
		},
	},
	async mounted() {
		await this.loadHarness();
	},
	methods: {
		async loadHarness() {
			try {
				this.isLoading = true;
				const response = await httpClient.getVideoCenterHarness({
					contextType: this.contextType,
					contextId:   this.contextId,
				});
				this.harness = response.data;
				this.activeFilters = Array.isArray(this.harness?.filters) ? this.harness.filters : [];
				this.applyInitialQueryState();
				if (!this.hasUrlPresetFilters && this.primaryView === 'live-and-upcoming') {
					await this.loadFilters();
				}
			} catch (error) {
				console.error(error);
			} finally {
				this.isLoading = false;
			}
		},
		applyInitialQueryState() {
			const urlPreset = parseVideoCenterUrlPreset(window.location.search);
			this.urlPresetFilters = urlPreset.filters;
			this.urlPresetSortOrder = urlPreset.sortOrder;

			if (this.hasUrlPresetFilters) {
				this.primaryView = 'replays-and-videos';
				return;
			}

			const requestedView = new URLSearchParams(window.location.search).get('view');

			if (requestedView === 'live-and-upcoming' && this.harness?.contextConfig?.liveUpcomingEnabled) {
				this.primaryView = 'live-and-upcoming';
				return;
			}

			this.primaryView = 'replays-and-videos';
		},
		buildFilterRequestParams() {
			return {
				contextType: this.contextType,
				contextId:   this.contextId,
				primaryView: this.primaryView,
				libraryMode: this.libraryMode,
				q:           this.searchValue || null,
				...this.selectedFilters,
			};
		},
		async loadFilters() {
			const requestId = ++this.filtersRequestId;

			try {
				const response = await httpClient.getVideoCenterFilters(this.buildFilterRequestParams());
				if (requestId !== this.filtersRequestId) {
					return;
				}

				this.activeFilters = Array.isArray(response.data?.filters) ? response.data.filters : [];
			} catch (error) {
				if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
					return;
				}

				console.error(error);
			}
		},
		handlePrimaryViewChange(value) {
			this.primaryView = value;
			this.loadFilters();
		},
		handleLibraryModeChange(value) {
			this.libraryMode = value;
			this.loadFilters();
		},
		handleSearchChange(value) {
			this.searchValue = value;
			if (this.hasUrlPresetFilters) {
				return;
			}

			this.loadFilters();
		},
		handleFilterChange({ key, value }) {
			this.selectedFilters = {
				...this.selectedFilters,
				[key]: value,
			};
			this.loadFilters();
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.video-center {
	padding-top: $sp6;
	margin-bottom: $sp7;
}

.video-center__loading {
	min-height: 180px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: $sp4;
}

.video-center__preset-search {
	margin-bottom: $sp6;
}
</style>
